import { Send, Loader2, CheckCircle2, XCircle, type LucideIcon } from "lucide-react";

import { Badge, type BadgeProps } from "@/components/ui/badge";

/**
 * Mirrors outbound_booking.status exactly (CHECK constraint: PENDING_SYNC | CONFIRMED | REJECTED |
 * FAILED — see the migration). "NOT_ENQUEUED" is a frontend-only fifth state for when no row exists
 * yet — never confuse it with PENDING_SYNC, which means a row exists and is actively retrying.
 */
export type HisSyncStatus = "NOT_ENQUEUED" | "PENDING_SYNC" | "CONFIRMED" | "REJECTED" | "FAILED";

const ICON: Record<HisSyncStatus, LucideIcon> = {
  NOT_ENQUEUED: Send,
  PENDING_SYNC: Loader2,
  CONFIRMED: CheckCircle2,
  REJECTED: XCircle,
  FAILED: XCircle,
};

const VARIANT: Record<HisSyncStatus, BadgeProps["variant"]> = {
  NOT_ENQUEUED: "muted",
  PENDING_SYNC: "warning",
  CONFIRMED: "success",
  REJECTED: "destructive",
  FAILED: "destructive",
};

/** Same {@link VARIANT} mapping, but CONFIRMED reads as neutral info (blue), not "success" green —
 * a cancel going through isn't the same kind of good news as a booking going through, and re-using
 * the booking-success color reads as "booking confirmed" to anyone glancing at the badge. */
const VARIANT_CANCEL: Record<HisSyncStatus, BadgeProps["variant"]> = {
  ...VARIANT,
  CONFIRMED: "info",
};

export const hisSyncStatusLabel: Record<HisSyncStatus, string> = {
  NOT_ENQUEUED: "Chưa gửi",
  PENDING_SYNC: "Đang xử lý",
  CONFIRMED: "Đã gửi HIS thành công",
  REJECTED: "Bị HIS từ chối",
  FAILED: "Gửi HIS thất bại",
};

/** Same {@link hisSyncStatusLabel} wording, but for the hủy-lịch (#18676) path. */
const hisSyncStatusLabelCancel: Record<HisSyncStatus, string> = {
  NOT_ENQUEUED: "Chưa gửi",
  PENDING_SYNC: "Đang hủy trên HIS",
  CONFIRMED: "Đã hủy trên HIS thành công",
  REJECTED: "HIS từ chối hủy",
  FAILED: "Hủy trên HIS thất bại",
};

export function HisSyncStatusBadge({
  status,
  action,
}: {
  status: HisSyncStatus;
  /** "BOOK" | "CANCEL" (#18676) — which verb `status` is reporting on. Defaults to booking wording. */
  action?: "BOOK" | "CANCEL";
}) {
  const Icon = ICON[status];
  const isCancel = action === "CANCEL";
  const label = isCancel ? hisSyncStatusLabelCancel[status] : hisSyncStatusLabel[status];
  const variant = isCancel ? VARIANT_CANCEL[status] : VARIANT[status];
  return (
    <Badge variant={variant} className="gap-1">
      <Icon aria-hidden="true" className={`h-3 w-3 ${status === "PENDING_SYNC" ? "animate-spin" : ""}`} />
      {label}
    </Badge>
  );
}
